/**
 * Expense claims, and approving them.
 *
 * `rebillable` and `invoiced` are different questions and both are columns.
 * Rebillable says the client agreed to carry this cost; invoiced says it has
 * already been put on a document. A claim that is rebillable and not yet
 * invoiced is the one finance is looking for.
 *
 * As with timesheets: **nobody approves their own claim**, the server refuses
 * it, and the action is withheld from those rows so the refusal is never how
 * somebody learns the rule.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Receipt, User, X } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  Modal,
  PageHeader,
  ServerTable,
  StatusChip,
  Textarea,
  api,
  can,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatMoney,
  toast,
  useCurrentUser,
  type ServerColumn,
} from '@connect/web-shared';

import { CLAIM_STATUS_TONES } from '../../lib/tones';
import { ExpenseClaimDialog } from './expense-claim-dialog';

export interface ExpenseRow {
  public_id: string;
  code: string;
  incurred_on: string;
  category: string;
  description: string;
  amount: string;
  currency: string;
  rebillable: boolean;
  status: string;
  employee_name: string;
  employee_public_id: string;
  project_name: string | null;
  invoiced: boolean;
  created_at: string;
}

interface TimesheetOptions {
  claim_statuses: { value: string; label: string }[];
  categories: { value: string; label: string }[];
}

const ENDPOINT = '/expenses';

export function ExpensesPage() {
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const [claiming, setClaiming] = useState(false);
  const [rejecting, setRejecting] = useState<ExpenseRow | null>(null);
  const [reason, setReason] = useState('');
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['timesheets', 'options'],
    queryFn: () => api.get<TimesheetOptions>('/timesheets/options'),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<ExpenseRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const decide = useMutation({
    mutationFn: ({ row, approve, why }: { row: ExpenseRow; approve: boolean; why?: string }) =>
      api.post(`${ENDPOINT}/${row.public_id}/decide`, { approve, reason: why ?? null }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success(variables.approve ? 'Claim approved' : 'Claim sent back');
      setRejecting(null);
      setReason('');
    },
    onError: (error) => {
      toast.error(
        'Could not record that',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  const columns = useMemo<ServerColumn<ExpenseRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Claim',
        filter: 'text',
        width: 125,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'incurred_on',
        header: 'Incurred',
        filter: 'date',
        width: 125,
        format: (value) => formatDate(value as string),
      },
      {
        field: 'employee_name',
        header: 'Who',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 160,
      },
      {
        field: 'description',
        header: 'What',
        icon: <Receipt className="size-3" />,
        filter: 'text',
        minWidth: 220,
      },
      {
        field: 'category',
        header: 'Category',
        filter: 'select',
        options: options.data?.categories,
        width: 140,
      },
      {
        field: 'amount',
        header: 'Amount',
        filter: 'number',
        numeric: true,
        width: 130,
        format: (value, row) => formatMoney(value as string, row.currency),
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: options.data?.claim_statuses,
        width: 120,
        render: (row) => <StatusChip value={row.status} tones={CLAIM_STATUS_TONES} />,
      },
      { field: 'project_name', header: 'Project', filter: 'select', minWidth: 170 },
      {
        field: 'rebillable',
        header: 'Rebillable',
        filter: 'boolean',
        align: 'center',
        width: 110,
        render: (row) =>
          row.rebillable ? (
            <StatusChip value="rebillable" tones={{ rebillable: 'info' }} />
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'invoiced',
        header: 'Invoiced',
        filter: 'boolean',
        align: 'center',
        width: 100,
        sortable: false,
        render: (row) =>
          row.invoiced ? (
            <StatusChip value="on an invoice" tones={{ 'on an invoice': 'success' }} />
          ) : (
            <span className="text-faint">—</span>
          ),
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Expenses"
        description="What has been spent, whether the client carries it, and whether it has been billed."
        actions={
          <Can permission="finance.expense.create">
            <Button size="sm" onClick={() => setClaiming(true)}>
              <Plus className="size-3.5" />
              New claim
            </Button>
          </Can>
        }
      />

      <ServerTable<ExpenseRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['expenses']}
        stateKey="finance.expenses"
        searchPlaceholder="Search by description, code or who"
        recentKey={recentKey}
        rowActions={(row) => {
          const mine = row.employee_public_id === user?.employee_public_id;
          if (row.status !== 'submitted' || mine || !can('finance.expense.approve')) return [];
          return [
            {
              label: 'Approve',
              icon: <Check className="size-3.5" />,
              onSelect: () => decide.mutate({ row, approve: true }),
            },
            {
              label: 'Send back',
              icon: <X className="size-3.5" />,
              onSelect: () => setRejecting(row),
            },
          ];
        }}
        rowDetailFields={(row) => [
          { label: 'Who', value: row.employee_name },
          { label: 'Project', value: row.project_name },
          { label: 'Category', value: row.category },
          { label: 'Amount', value: formatMoney(row.amount, row.currency) },
          { label: 'Rebillable', value: row.rebillable ? 'Yes' : 'No' },
          { label: 'Invoiced', value: row.invoiced ? 'Yes' : 'Not yet' },
        ]}
        emptyTitle="No claims"
        emptyDescription="An approved, rebillable claim can be pulled straight onto a client's invoice."
        emptyAction={
          <Can permission="finance.expense.create">
            <Button size="sm" onClick={() => setClaiming(true)}>
              <Plus className="size-3.5" />
              Claim an expense
            </Button>
          </Can>
        }
      />

      <ExpenseClaimDialog
        open={claiming}
        onClose={() => setClaiming(false)}
        onSaved={(publicId) => {
          setRecentKey(publicId);
          setClaiming(false);
        }}
      />

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Send this claim back"
        description={rejecting ? `${rejecting.code} — ${rejecting.description}` : undefined}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={decide.isPending}
              disabled={!reason.trim()}
              onClick={() => {
                if (rejecting) {
                  decide.mutate({ row: rejecting, approve: false, why: reason.trim() });
                }
              }}
            >
              Send it back
            </Button>
          </>
        }
      >
        <Textarea
          label="Why"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="They see this. Without it the claim comes back unchanged."
          placeholder="No receipt attached."
        />
      </Modal>
    </div>
  );
}
